import React from 'react';

import { Input, Link, LocationSelect, NewsletterCheckbox, styled } from '@asicsdigital/oneasics-blocks';
import { useSelector } from 'react-redux';

import EmailAddress from '../email-address/EmailAddress';
import { FormDisclaimerText } from '../form/shared';

const EmailAddressContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const errorDefaults = {
  email: {
    message: undefined,
  },
  password: {
    message: undefined,
  },
  fullName: {
    message: undefined,
  },
  birthday: {
    message: undefined,
  },
  country: {
    message: undefined,
  },
  gender: {
    message: undefined,
  },
};

const formCopyDefaults = {
  birthday: {
    label: '',
    placeholder: '',
  },
  country: {
    label: '',
    options: { a: 'a' },
  },
  email: {
    label: '',
    placeholder: '',
  },
  firstName: {
    label: '',
    placeholder: '',
  },
  fullName: {
    label: '',
    placeholder: '',
  },
  gender: {
    label: '',
    options: { a: 'a' },
  },
  lastName: {
    label: '',
    placeholder: '',
  },
  newsletterOpt: '',
  optional: '',
  password: {
    label: '',
    placeholder: '',
  },
};

/**
 * User info displayed as input fields meant to be used in a form.
 * The *Visible parameters control whether the input is actually present, as opposed to being visible/hidden.
 */
const UserInfoInputs = ({
  autoOptIn = false,
  birthdayInitialValue = '',
  birthdayVisible = true,
  clearErrors,
  control,
  countryVisible = false,
  emailAddressShown = false,
  disableInputs = false,
  emailVisible = true,
  errors = errorDefaults,
  firstNameRequired = false,
  firstNameVisible = false,
  formCopy = formCopyDefaults,
  fullNameRequired = false,
  fullNameVisible = false,
  genderRequired = false,
  genderVisible = false,
  isTrackingCountry = false,
  isTrackingGender = false,
  lastNameRequired = false,
  lastNameVisible = false,
  localeCountry = '',
  newsletterOptVisible = false,
  newsLetterOptCopy = '',
  newPassword = false,
  passwordVisible = false,
  preCheckboxDisclaimerText,
  region = '',
  register,
  showPasswordValidation = false,
  showPlaceholders = false,
}) => {
  const {
    user: { email },
    ui: { emailFromLander },
  } = useSelector((state) => state);

  const renderedNameList = localeCountry === 'JP' || region === 'AJP' ? ['lastName', 'firstName'] : ['firstName', 'lastName'];
  const getOptionalForName = (isFirstName) => {
    if (isFirstName) {
      return (firstNameRequired ? '' : ` (${formCopy.optional})`);
    }

    return (lastNameRequired ? '' : ` (${formCopy.optional})`);
  };

  const NewsletterCheckboxWrapper = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes.xxSmall,
    lineHeight: '20px',
    paddingTop: preCheckboxDisclaimerText ? '0px' : '22px',
    ...(autoOptIn && { fontSize: theme.fontSizes.xxSmall }),
    color: theme.colors.grey[600],
  }));

  const StyledLink = styled(Link) ({
    margin: '0px 16px',
    padding: 0,
  });

  return (
    <>
      {((emailFromLander || email) && emailAddressShown) &&
        <EmailAddressContainer id="email-address-container">
          <EmailAddress>{emailFromLander || email}</EmailAddress>

          {emailFromLander &&
            <StyledLink
              fontSize="xxSmall"
              id="email-edit-button"
              path="/"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
            >
              {formCopy.edit}
            </StyledLink>
          }
        </EmailAddressContainer>
      }

      <Input
        aria-describedby="error-email-required"
        aria-invalid={errors?.email ? 'true' : 'false'}
        clearErrors={clearErrors}
        control={control}
        disabled={disableInputs}
        errorMessage={errors?.email?.message}
        id="email"
        label={formCopy?.email?.label}
        name="email"
        placeholder={showPlaceholders ? formCopy.email.placeholder : undefined}
        register={register}
        type="email"
        visible={emailVisible} // Visible means the input is present in the form
      />

      {countryVisible &&
        <div id="location-container">
          <LocationSelect
            control={control}
            disabled={disableInputs}
            editText={formCopy.edit}
            errorMessage={errors?.country?.message}
            id="country"
            isTracking={isTrackingCountry}
            name="country"
            options={formCopy.country.options}
            register={register}
          />
        </div>
      }

      {(firstNameVisible || lastNameVisible) &&
        <>
          {renderedNameList.map((name) => {
            const isFirstName = name === 'firstName';

            return (
              <Input
                aria-describedby={`error-${name}-required`}
                aria-invalid={errors[name] ? 'true' : 'false'}
                control={control}
                disabled={disableInputs}
                errorMessage={errors[name]?.message}
                id={name}
                key={name}
                label={formCopy[name]?.label + getOptionalForName(isFirstName)}
                name={name}
                placeholder={showPlaceholders ? formCopy[name].placeholder : undefined}
                register={register}
                type="text"
                visible={isFirstName ? firstNameVisible : lastNameVisible}
              />
            );
          })}
        </>
      }

      <Input
        aria-describedby="error-fullName-required"
        aria-invalid={errors?.fullName ? 'true' : 'false'}
        control={control}
        disabled={disableInputs}
        errorMessage={errors?.fullName?.message}
        id="fullName"
        label={formCopy?.fullName?.label + (fullNameRequired ? '' : ` (${formCopy.optional})`)}
        name="fullName"
        placeholder={showPlaceholders ? formCopy.fullName.placeholder : undefined}
        register={register}
        type="text"
        visible={fullNameVisible}
      />

      <Input
        aria-describedby="error-birthday-required"
        aria-invalid={errors?.birthday ? 'true' : 'false'}
        clearErrors={clearErrors}
        control={control}
        disabled={disableInputs}
        errorMessage={errors?.birthday?.message}
        id="birthday"
        initialValue={birthdayInitialValue}
        label={formCopy?.birthday?.label}
        name="birthday"
        placeholder={formCopy.birthday.placeholder}
        register={register}
        type="birthday"
        visible={birthdayVisible}
      />

      <Input
        aria-describedby="error-password-required"
        aria-invalid={errors?.password ? 'true' : 'false'}
        clearErrors={clearErrors}
        control={control}
        disabled={disableInputs}
        errorMessage={errors?.password?.message}
        id="password"
        label={newPassword ? formCopy?.newPassword?.label : formCopy?.password?.label}
        name="password"
        passwordRequirementText={showPasswordValidation && formCopy?.password?.helperText}
        placeholder={showPlaceholders ? formCopy?.password?.placeholder : undefined}
        register={register}
        type="password"
        visible={passwordVisible}
      />

      <Input
        aria-describedby="error-gender-required"
        aria-invalid={errors?.gender ? 'true' : 'false'}
        control={control}
        disabled={disableInputs}
        errorMessage={errors?.gender?.message}
        id="gender"
        isTracking={isTrackingGender}
        label={formCopy?.gender?.label + (genderRequired ? '' : ` (${formCopy.optional})`)}
        name="gender"
        options={formCopy.gender.options}
        register={register}
        type="select"
        visible={genderVisible}
      />

      {preCheckboxDisclaimerText?.length > 1 &&
        <FormDisclaimerText>
          {preCheckboxDisclaimerText}
        </FormDisclaimerText>
      }

      {newsletterOptVisible && !autoOptIn &&
        <NewsletterCheckboxWrapper>
          <NewsletterCheckbox
            id="newsletter"
            register={register}
          >
            {newsLetterOptCopy}
          </NewsletterCheckbox>
        </NewsletterCheckboxWrapper>
      }

      {autoOptIn &&
        <NewsletterCheckboxWrapper>
          {newsLetterOptCopy}
        </NewsletterCheckboxWrapper>
      }
    </>
  );
};

export default UserInfoInputs;
